import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Label } from 'lib/Label';
import { BaseSelectStyleProps } from 'lib/BaseSelect/BaseSelect.types';
import { changeDealData } from 'store/dealManagement/reducer';
import { InputTheme } from 'components/common/types/Input.types';
import AsyncSelect from 'lib/AsyncSelect';
import { AsyncSelectLoadDataHandler, SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { Store } from 'components/common/types/Store.types';
import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import { BrandWithProductCategory, DealStatus, SelectedBrand } from 'components/common/types/Deal.types';
import { getBrand } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import usePendoTracking from 'customHooks/usePendoTracking';

interface SelectBrandProps extends BaseSelectStyleProps {
  brands: BrandWithProductCategory[];
  onBrandChange: (value: SelectedBrand | null) => void;
  dataTestId?: string;
  placeholder?: string;
  withLabel?: boolean;
  isRequired?: boolean;
}

export const SelectBrand = ({
  brands,
  onBrandChange,
  dataTestId,
  placeholder,
  theme,
  size,
  padding,
  shadow,
  shape,
  withLabel = false,
  isDisabled = false,
  isRequired = false,
}: SelectBrandProps): React.ReactElement => {
  const brand = useSelector(getBrand);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);

  const dispatch = useDispatch();
  const { onLoadBrands } = useLoadLookup(0);
  const readOnly = useSelector(getDealFormReadOnly);
  const { isCampaignReadOnly } = useIsReadOnly();
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const { getPendoDataId } = usePendoTracking();

  const loadDataHandler: AsyncSelectLoadDataHandler<BrandWithProductCategory> = (_, query) => {
    if (brands.length) {
      return Promise.resolve(brands.filter(({ name }) => name.toLowerCase().includes(query.toLowerCase())));
    }

    return onLoadBrands(_, query);
  };

  const onSelect: SelectHandler<SelectedBrand> = (dropdownName, value: SelectedBrand | null) => {
    dispatch(changeDealData({ [dropdownName]: value }));
    onBrandChange(value);
  };

  return (
    <>
      {withLabel ? <Label id="brand" theme={InputTheme.FLAT_GRAY} label="Brand" isRequired={isRequired} /> : null}
      <Tooltip tooltip={brand?.name} direction={TooltipDirection.BOTTOM} isHiddenOnFocus>
        <AsyncSelect
          dataTestId={dataTestId}
          name="brand"
          placeholder={placeholder}
          selectedValue={brand}
          loadData={loadDataHandler}
          onSelect={onSelect}
          defaultOptions={brands}
          isDisabled={isDisabled || (readOnly && !isPendingReservation) || isCampaignReadOnly}
          theme={theme}
          size={size}
          padding={padding}
          shadow={shadow}
          shape={shape}
          dataPendoId={getPendoDataId('Brand')}
        />
      </Tooltip>
    </>
  );
};
